import { PlatformCardInsights } from '@/__generated__/graphql';

/**
 * RI value color mapping based on design specification
 */

type FleetHealthFilters = {
  [category: string]: {
    [serviceType: string]: {
      ri0?: number;
      ri1?: number;
      ri2?: number;
      ri3?: number;
      ri4?: number;
      ri5?: number;
    };
  };
};

/**
 * Calculates aggregated RI values from fleetHealthFilters
 * Sums up all RI values (ri0-ri5) across all categories and service types
 *
 * @param fleetHealthFilters - The fleetHealthFilters object from platform document
 * @param selectedDeploymentTypes - Optional array of deployment types to filter by
 * @param categoryDataTypes - Optional mapping of category to its selected RI levels for per-category filtering
 * @returns Object with aggregated RI values
 */
function calculateRIAggregatesFromFleetHealthFilters(
  fleetHealthFilters: FleetHealthFilters | null | undefined,
  selectedDeploymentTypes?: string[],
  categoryDataTypes?: Record<string, string[]>
): Record<string, number> {
  if (!fleetHealthFilters || typeof fleetHealthFilters !== 'object') {
    return {
      ri0: 0,
      ri1: 0,
      ri2: 0,
      ri3: 0,
      ri4: 0,
      ri5: 0,
    };
  }

  // Initialize aggregators for each RI value
  type RIKey = 'ri0' | 'ri1' | 'ri2' | 'ri3' | 'ri4' | 'ri5';
  const riAggregates: Record<RIKey, number> = {
    ri0: 0,
    ri1: 0,
    ri2: 0,
    ri3: 0,
    ri4: 0,
    ri5: 0,
  };

  // Iterate through all categories (coatings_manager, structure, uncategorized, etc.)
  Object.entries(fleetHealthFilters).forEach(([categoryKey, categoryData]) => {
    // Skip if we have a deployment filter and this category is not selected
    if (selectedDeploymentTypes && selectedDeploymentTypes.length > 0) {
      if (!selectedDeploymentTypes.includes(categoryKey)) {
        return;
      }
    }

    // Get the RI levels allowed for this specific category
    const categoryRiLevels = categoryDataTypes?.[categoryKey];
    const riLevelFilter = categoryRiLevels
      ? new Set(categoryRiLevels.map((ri) => ri.toLowerCase()))
      : null;

    if (!categoryData || typeof categoryData !== 'object') {
      return;
    }

    // Get enabled service types from categoryDataTypes (if any service groups are specified)
    // ONLY apply service type filtering for "pressure" category
    const enabledServiceTypes = categoryDataTypes
      ? Object.keys(categoryDataTypes).filter(
          (key) => key !== categoryKey && categoryDataTypes[key]
        )
      : [];
    const hasServiceTypeFilter = enabledServiceTypes.length > 0 && categoryKey === 'pressure';

    // Iterate through all service types within each category
    Object.entries(categoryData).forEach(([serviceType, serviceData]) => {
      // If we have a service type filter (for pressure only) and this service type is not enabled, skip it
      if (hasServiceTypeFilter && !enabledServiceTypes.includes(serviceType)) {
        return;
      }

      if (!serviceData || typeof serviceData !== 'object') {
        return;
      }

      // Aggregate RI values
      (Object.keys(riAggregates) as RIKey[]).forEach((riKey) => {
        // Skip if we have an RI level filter for this category and this level is not selected
        if (riLevelFilter && !riLevelFilter.has(riKey)) {
          return;
        }

        const value = serviceData[riKey];
        if (typeof value === 'number' && !Number.isNaN(value)) {
          riAggregates[riKey] += value;
        }
      });
    });
  });

  return riAggregates;
}

/**
 * Finds an existing insight from platformCardInsights array by matching RI name
 *
 * @param platformCardInsights - Array of existing platform insights
 * @param riKey - The RI key to match (e.g., 'ri0', 'ri1')
 * @returns The matching insight or null if not found
 */
function findExistingInsightByRIKey(
  platformCardInsights: PlatformCardInsights[] | null | undefined,
  riKey: string
): PlatformCardInsights | null {
  if (!platformCardInsights || platformCardInsights.length === 0) {
    return null;
  }

  // Normalize riKey to match the name format in insights (e.g., "ri0" -> "Ri0")
  const normalizedRIKey = riKey.charAt(0).toUpperCase() + riKey.slice(1);

  // Find insight with matching group and name
  return (
    platformCardInsights.find((insight) => {
      const insightGroup = insight.group?.toLowerCase();
      const insightName = insight.name?.toLowerCase();
      // Use 'coating-health' as the actual group key
      const isCoatingGroup = insightGroup === 'coating-health';
      const matchesRIKey =
        insightName === riKey.toLowerCase() || insightName === normalizedRIKey.toLowerCase();

      return isCoatingGroup && matchesRIKey;
    }) || null
  );
}

/**
 * Maps fleetHealthFilters data to platformCardInsights format for coating condition
 * Aggregates all RI values across all categories and service types
 * Gets displayConfig and color from existing platformCardInsights array
 *
 * @param fleetHealthFilters - The fleetHealthFilters object from platform document
 * @param platformCardInsights - Existing platformCardInsights array to get displayConfig from
 * @returns Array of PlatformCardInsights for coating condition
 */
export function processFleetHealthFiltersToCoatingInsights(
  fleetHealthFilters: FleetHealthFilters | null | undefined,
  platformCardInsights: PlatformCardInsights[] | null | undefined
): PlatformCardInsights[] {
  if (!fleetHealthFilters || typeof fleetHealthFilters !== 'object') {
    return [];
  }

  // Initialize aggregators for each RI value
  type RIKey = 'ri0' | 'ri1' | 'ri2' | 'ri3' | 'ri4' | 'ri5';
  const riAggregates: Record<RIKey, number> = {
    ri0: 0,
    ri1: 0,
    ri2: 0,
    ri3: 0,
    ri4: 0,
    ri5: 0,
  };

  // Iterate through all categories (pressure, uncategorized, structure, coatings_manager, etc.)
  Object.values(fleetHealthFilters).forEach((categoryData) => {
    if (!categoryData || typeof categoryData !== 'object') {
      return;
    }

    // Iterate through all service types within each category
    Object.values(categoryData).forEach((serviceData) => {
      if (!serviceData || typeof serviceData !== 'object') {
        return;
      }

      // Aggregate RI values
      (Object.keys(riAggregates) as RIKey[]).forEach((riKey) => {
        const value = serviceData[riKey];
        if (typeof value === 'number' && !Number.isNaN(value)) {
          riAggregates[riKey] += value;
        }
      });
    });
  });

  // Create platformCardInsights entries
  const insights: PlatformCardInsights[] = [];

  // Process RI values in order: Ri0, Ri1, Ri2, Ri3, Ri4, Ri5
  const riOrder: RIKey[] = ['ri0', 'ri1', 'ri2', 'ri3', 'ri4', 'ri5'];

  riOrder.forEach((riKey) => {
    const value = riAggregates[riKey];

    if (value > 0) {
      // Find existing insight to get displayConfig and color
      const existingInsight = findExistingInsightByRIKey(platformCardInsights, riKey);

      // Use displayConfig from existing insight if available, otherwise use defaults
      const displayConfig = existingInsight?.displayConfig || {
        color: '#808080', // Default gray color if not found
        unit: 'SQUARE_METER',
        barIndex: 0,
        listIndex: 0,
        divider: false,
      };

      insights.push({
        group: 'coating-health', // Use 'coating-health' as the actual group key
        name: riKey.charAt(0).toUpperCase() + riKey.slice(1), // Capitalize: "ri0" -> "Ri0"
        value: value.toFixed(2), // Convert to string with 2 decimal places
        displayConfig: {
          color: displayConfig.color,
          unit: displayConfig.unit || 'SQUARE_METER',
          barIndex: displayConfig.barIndex ?? 0,
          listIndex: displayConfig.listIndex ?? 0,
          divider: displayConfig.divider ?? false,
        },
        filterOptions: [],
      });
    }
  });

  return insights;
}

/**
 * Updates existing coating condition platformCardInsights with calculated RI values from fleetHealthFilters
 * Preserves the existing displayConfig from the coating condition entries
 *
 * @param platformCardInsights - Existing platformCardInsights array
 * @param fleetHealthFilters - The fleetHealthFilters object from platform document
 * @param selectedDeploymentTypes - Optional array of deployment types to filter by
 * @param categoryDataTypes - Optional mapping of category to its selected RI levels for per-category filtering
 * @returns Updated platformCardInsights array with coating condition values updated
 */
export function updateCoatingHealthInsightsFromFleetHealthFilters(
  platformCardInsights: PlatformCardInsights[] | null | undefined,
  fleetHealthFilters: FleetHealthFilters | null | undefined,
  selectedDeploymentTypes?: string[],
  categoryDataTypes?: Record<string, string[]>
): PlatformCardInsights[] {
  if (!platformCardInsights || platformCardInsights.length === 0) {
    return [];
  }

  // Calculate aggregated RI values with optional filters
  const riAggregates = calculateRIAggregatesFromFleetHealthFilters(
    fleetHealthFilters,
    selectedDeploymentTypes,
    categoryDataTypes
  );

  // Update existing coating condition insights with calculated values
  return platformCardInsights.map((insight) => {
    // Only update insights with group "coating-health" (the actual group key)
    const insightGroup = insight.group.toLowerCase();
    if (insightGroup === 'coating-health') {
      const riKey = insight.name.toLowerCase();

      // Check if this is a valid RI key (ri0-ri5)
      if (riKey in riAggregates) {
        const calculatedValue = riAggregates[riKey];

        return {
          ...insight,
          group: 'coating-health', // Keep 'coating-health' as the group key
          value: calculatedValue.toFixed(2), // Update value, keep existing displayConfig
        };
      }
    }

    // Return unchanged for non-coating condition insights
    return insight;
  });
}
